import { useEffect, useState } from 'react';
import { useNavigate } from '@/lib/router-compat';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requireAdmin?: boolean;
}

export default function ProtectedRoute({ children, requireAdmin = false }: ProtectedRouteProps) {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  // Server-verified admin status. `null` = still verifying.
  const [adminVerified, setAdminVerified] = useState<boolean | null>(requireAdmin ? null : false);

  useEffect(() => {
    if (!requireAdmin) return;
    let cancelled = false;

    if (!user) {
      setAdminVerified(null);
      return;
    }

    setAdminVerified(null);
    supabase.rpc('is_admin').then(({ data, error }) => {
      if (!cancelled) setAdminVerified(!error && data === true);
    });

    return () => {
      cancelled = true;
    };
  }, [user, requireAdmin]);

  const verifying = requireAdmin && !!user && adminVerified === null;

  useEffect(() => {
    if (!loading && !verifying) {
      if (!user) {
        navigate('/auth');
      } else if (requireAdmin && !adminVerified) {
        navigate('/dashboard');
      }
    }
  }, [user, loading, verifying, adminVerified, requireAdmin, navigate]);

  if (loading || verifying) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return null;
  }

  if (requireAdmin && !adminVerified) {
    return null;
  }

  return <>{children}</>;
}
